import React, { useMemo } from 'react';
import Lottie from 'react-lottie';
import './ThemedLottie.scss';
import { getThemeColor, getThemeColorVar } from '../../../utilities/theme/ThemeColorEnum';

/**
 * Parse a CSS color (hex / rgb / rgba) into Lottie [r, g, b] values (0–1).
 */
function parseCssColorToLottie(cssColor) {
  if (!cssColor || typeof cssColor !== 'string') {
    return null;
  }

  const value = cssColor.trim();

  const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    return [r, g, b];
  }

  const rgbMatch = value.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)$/i
  );
  if (rgbMatch) {
    return [
      Number(rgbMatch[1]) / 255,
      Number(rgbMatch[2]) / 255,
      Number(rgbMatch[3]) / 255,
    ];
  }

  return null;
}

/**
 * Recursively replace fill/stroke colors inside a Lottie shapes tree.
 */
function applyColorToShapes(shapes, lottieColor) {
  if (!Array.isArray(shapes)) {
    return;
  }

  shapes.forEach((shape) => {
    if ((shape.ty === 'fl' || shape.ty === 'st') && shape.c && Array.isArray(shape.c.k)) {
      const keyframes = shape.c.k;
      // Static color: [r, g, b] or [r, g, b, a]
      if (typeof keyframes[0] === 'number') {
        const alpha = keyframes.length > 3 ? keyframes[3] : undefined;
        shape.c.k = alpha !== undefined ? [...lottieColor, alpha] : [...lottieColor];
      }
    }

    if (shape.it) {
      applyColorToShapes(shape.it, lottieColor);
    }
  });
}

/**
 * Clone animationData and recolor layers by `ind` using theme tokens.
 * @param {object} animationData
 * @param {Record<number|string, string>} layerColors - map of layer ind → ThemeColorEnum
 */
function applyLayerColors(animationData, layerColors) {
  if (!animationData || !layerColors) {
    return animationData;
  }

  const cloned = JSON.parse(JSON.stringify(animationData));
  if (!Array.isArray(cloned.layers)) {
    return cloned;
  }

  cloned.layers.forEach((layer) => {
    const themeToken =
      layerColors[layer.ind] ?? layerColors[String(layer.ind)];
    if (!themeToken) {
      return;
    }

    const cssColor = getThemeColor(themeToken, '');
    const lottieColor = parseCssColorToLottie(cssColor);
    if (!lottieColor) {
      return;
    }

    applyColorToShapes(layer.shapes, lottieColor);
  });

  return cloned;
}

/**
 * Lottie wrapper with CMS theme color override.
 *
 * @example Single color (CSS override on all shapes)
 * <ThemedLottie animationData={PaperPlane} color={ThemeColorEnum.PRIMARY} width={250} />
 *
 * @example Per-layer colors (by Lottie layer `ind`)
 * <ThemedLottie
 *   animationData={loader}
 *   layerColors={{ 1: ThemeColorEnum.PRIMARY, 2: ThemeColorEnum.LIGHT }}
 * />
 */
const ThemedLottie = ({
  animationData,
  width,
  height,
  loop = false,
  autoplay = true,
  isStopped,
  isPaused,
  className = '',
  style,
  color,
  layerColors,
  rendererSettings,
  eventListeners,
  resizeMode = 'no-resize',
}) => {
  const hasLayerColors =
    layerColors && Object.keys(layerColors).length > 0;
  // Prefer per-layer patching; fall back to single-color CSS override
  const useCssRecolor = Boolean(color) && !hasLayerColors;

  const themedAnimationData = useMemo(() => {
    if (!hasLayerColors) {
      return animationData;
    }
    return applyLayerColors(animationData, layerColors);
  }, [animationData, layerColors, hasLayerColors]);

  const wrapperStyle = useMemo(() => {
    if (!useCssRecolor) {
      return style;
    }

    return {
      ...style,
      '--lottie-fill': getThemeColorVar(color),
      '--lottie-stroke': getThemeColorVar(color),
    };
  }, [useCssRecolor, color, style]);

  const options = useMemo(
    () => ({
      loop,
      autoplay,
      animationData: themedAnimationData,
      rendererSettings,
      resizeMode,
    }),
    [loop, autoplay, themedAnimationData, rendererSettings, resizeMode]
  );

  const wrapperClassName = [
    'themed-lottie',
    useCssRecolor ? 'themed-lottie--recolor-css' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClassName} style={wrapperStyle}>
      <Lottie
        options={options}
        width={width}
        height={height}
        isStopped={isStopped}
        isPaused={isPaused}
        eventListeners={eventListeners}
      />
    </div>
  );
};

export default ThemedLottie;
